import { MediaMatcher } from '@angular/cdk/layout';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, AsyncValidatorFn, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { AppConfig } from '@app/core/app.config';
import { ActionEnum, EventOriginEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import {
  FIELD_ASYNC_PRECONDITIONS_INJECTION,
  FIELD_CONFIG_INJECTION,
  FIELD_ENTITY_INJECTION,
  FIELD_EVENTS_ORIGIN,
  FIELD_EXTRA_DATA,
  FIELD_GROUP_CONFIG_INJECTION,
  FIELD_PERMISSIONS_INJECTION,
  FIELD_PRECONDITIONS_INJECTION,
  SECTION_CREATES_ENTITY_INJECTION,
  SECTION_ENTITY_TYPE_INJECTION
} from '@app/core/model/other/field-config';
import { FieldValidator } from '@app/core/model/other/field-validator';
import { AppManager } from '@app/core/services/managers/app.manager';
import { FormStateService } from '@app/shared/components/form-builder/form-state.service';
import {
  customCssBuilderForEntity,
  getValue,
  isNullOrEmpty,
  setValue,
  translateValueBuilder
} from '@app/shared/extra/utils';
import { SingleEditService } from '@app/shared/services/single-edit-service';
import { ValidationService } from '@app/shared/services/validation.service';
import { TranslateService } from '@ngx-translate/core';
import { AccessManager } from '@services/managers/access.manager';
import structuredClone from '@ungap/structured-clone';
import fastDeepEqual from 'fast-deep-equal/es6';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export enum FieldStateMode {
  READ,
  EDIT,
  EMPTY,
  SAVING,
  AFTER_SAVE,
  ERROR
}

/**
 * Shared class between field groups and fields in which are defined
 * a few common properties and methods used to change the state of said fieldgroup or field
 */
@Component({template: '', standalone: false})
// eslint-disable-next-line @angular-eslint/component-class-suffix
abstract class SharedBuilder {
  protected data = inject(FIELD_EXTRA_DATA);
  protected eventsOrigin = <EventOriginEnum>inject(FIELD_EVENTS_ORIGIN);
  protected entity = inject(FIELD_ENTITY_INJECTION);
  protected preconditionsForEdition = inject(FIELD_PRECONDITIONS_INJECTION);
  protected permissionsForEdition = inject(FIELD_PERMISSIONS_INJECTION);
  protected isCreatingEntity = <boolean>inject(SECTION_CREATES_ENTITY_INJECTION);
  protected formStateService = inject(FormStateService);
  protected appManager = inject(AppManager);
  protected appConfig = inject(AppConfig);
  protected accessManager = inject(AccessManager);
  protected singleEditService = inject(SingleEditService);
  protected analyticsService = inject(AnalyticsService);
  protected validationService = inject(ValidationService);
  protected media = inject(MediaMatcher);
  protected destroyRef = inject(DestroyRef);

  public FieldMode = FieldStateMode;
  public currentMode: FieldStateMode;

  public abstract edit(): void;

  public abstract read(): void;

  public abstract empty(): void;

  public abstract save(): void;

  public abstract cancel(): void;

  /**
   * Retrieves the changes object group's change subject from the FormStateService.
   * @return Field group's change subject.
   */
  public getFormChanges(keys: string[]): Subject<any> {
    return this.formStateService.getPath(['changes', ...keys], {
      customPath: this.data.propertiesPath
    });
  }
}

/**
 * Shared class between all field groups
 * including all grouped, singles and the custom ones e.g. address
 */
@Component({template: '', standalone: false})
// eslint-disable-next-line @angular-eslint/component-class-suffix
export abstract class AbstractFieldGroupBuilder extends SharedBuilder {
  private touchScreenQuery: MediaQueryList;
  public fieldGroup = inject(FIELD_GROUP_CONFIG_INJECTION);

  constructor() {
    super();
    this.touchScreenQuery = this.media.matchMedia('(pointer: coarse)');
  }

  /**
   * On touch screen devices only, toggle the tooltip after pressing the icon.
   * @param tooltip MatTooltip to toggle.
   */
  public onClickTooltip(tooltip: MatTooltip): void {
    if (this.touchScreenQuery.matches) {
      tooltip.toggle();
    }
  }

  /**
   * Reacts to any event on a focused element
   * @param {Event} event
   */
  public onActivate(event: Event): void {
    this.activate();
    event.stopImmediatePropagation();
    event.stopPropagation();
  }

  /**
   * Responds to an Escape keypress on a focused element
   * @param {Event} event
   */
  public onEscapeCancel(event: Event): void {
    this.cancel();
    event.stopImmediatePropagation();
    event.stopPropagation();
  }

  /**
   * Sets the current state of the field group as saving
   * and copies the data from the current value to the initialvalue as to avoid simply copying references
   */
  public save(): void {
    this.getFieldGroupState().next(this.FieldMode.SAVING);
    this.formStateService.save(this.fieldGroup, this.fieldGroup.code, this.data.formId, this.data.propertiesPath);
  }

  /**
   * Adds indirection to the save logic if need be by separating the save event from the save logic
   */
  public onClickSave(): void {
    this.analyticsService.trackInlineActionEvent(this.fieldGroup.code, ActionEnum.SAVE, this.eventsOrigin);
    this.save();
  }

  /**
   * Replaces the data of the current value with the data in the initialvalue
   * and retrieves the next state based on whether the current value is empty or not
   */
  public cancel(): void {
    this.setFieldGroupValue(structuredClone(this.getFieldGroupInitialValue()));
    this.getNextState();
  }

  /**
   * Adds indirection to the cancel logic because otherwise all fields would send
   * a cancel analytics event when switching from one field to another
   */
  public onClickCancel(): void {
    this.analyticsService.trackInlineActionEvent(this.fieldGroup.code, ActionEnum.CANCEL, this.eventsOrigin);
    this.cancel();
  }

  /**
   * Enables the field to be edited in whichever way it functions
   */
  public edit(): void {
    this.analyticsService.trackInlineActionEvent(this.fieldGroup.code, ActionEnum.SELECT, this.eventsOrigin);
    this.getFieldGroupState().next(this.FieldMode.EDIT);
    this.singleEditService.singleEditSubject.next(this.fieldGroup.code);
  }

  /**
   * Sets the field in read mode which can be clicked to turn it into the edit mode
   */
  public read(): void {
    this.getFieldGroupState().next(this.FieldMode.READ);
  }

  /**
   * Displays a link which can be clicked to turn it into the edit mode
   */
  public empty(): void {
    this.getFieldGroupState().next(this.FieldMode.EMPTY);
  }

  /**
   * Set up the hooks to which the field must react in order to update themselves
   */
  protected setupHooks(): void {
    // Hook to a field becoming into an edit mode
    this.singleEditService.singleEditSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((groupCode?: string) => {
      if (this.fieldGroup.code !== groupCode) {
        this.cancel();
      }
    });
  }

  /**
   * Checks the current value of a field and determines which state it should be next
   * based on if every property of the object is undefined
   */
  protected getNextState(): void {
    const containsComputedField = this.fieldGroup.children
      .filter(f => f.field.computed)
      .some(f => !!getValue(this.entity, f.fieldPath));
    const isEmpty = Object.values(this.getFieldGroupValue()).every((value) => {
      return isNullOrEmpty(value);
    });
    if (isEmpty && !containsComputedField) {
      this.empty();
    } else {
      this.read();
    }
  }

  /**
   * Either activate the field into editing mode or save it if you were editing it
   */
  private activate(): void {
    if (this.accessManager.hasAllNeededPermissions(this.permissionsForEdition) && this.preconditionsForEdition) {
      this.edit();
    }
  }

  protected refreshEntity(): void {
    this.entity = this.data.propertiesPath
      ? this.appManager.currentEntity[this.data.propertiesPath]
      : this.appManager.currentEntity;
  }

  /**
   * Retrieves data about the field group, stored at the provided path in the FormStateService.
   * @param keys Path in the FormStateService.
   * @return Data at given path.
   */
  public getFieldGroupInfo(keys: string[]): any {
    return this.formStateService.getPath([this.fieldGroup.code, ...keys], {
      customPath: this.data.propertiesPath
    });
  }

  /**
   * Retrieves the field group's value from the FormStateService.
   * @return Field group's value.
   */
  public getFieldGroupValue(): any {
    return this.getFieldGroupInfo(['value']);
  }

  /**
   * Retrieves the field group's initial value from the FormStateService.
   * @return Field group's initial value.
   */
  public getFieldGroupInitialValue(): any {
    return this.getFieldGroupInfo(['initialValue']);
  }

  /**
   * Retrieves the field group's state from the FormStateService.
   * @return Field group's state.
   */
  public getFieldGroupState(): Subject<FieldStateMode> {
    return this.getFieldGroupInfo(['state']);
  }

  /**
   * Store the value at the given path in the FormStateService.
   * @param keys Path where to store the value.
   * @param value Data to be stored.
   */
  public setFieldGroupInfo(keys: string[], value: any): void {
    this.formStateService.setPath([this.fieldGroup.code, ...keys], value, {
      customPath: this.data.propertiesPath
    });
  }

  /**
   * Sets the field group's value in the FormStateService.
   * @param value Value to be stored.
   */
  public setFieldGroupValue(value: any): void {
    this.setFieldGroupInfo(['value'], value);
  }

  /**
   * Sets the field group's initial value in the FormStateService.
   * @param value Initial value to be stored.
   */
  public setFieldGroupInitialValue(value: any): void {
    this.setFieldGroupInfo(['initialValue'], value);
  }
}

/**
 * Shared class between all fields
 * of every type such as numeric, text and list
 */
@Component({template: '', standalone: false})
// eslint-disable-next-line @angular-eslint/component-class-suffix
export abstract class AbstractFieldBuilder<T extends AbstractControl> extends SharedBuilder {
  protected fieldGroupCode: string;
  protected propertiesPath: string;
  protected isSingleField: boolean;
  protected viewToggled: boolean;
  protected editToggled: boolean;
  protected focusable: boolean;
  protected fieldInitValue: any;
  protected requestFocus: boolean;
  public form: FormGroup<{ field: T, [key: string]: AbstractControl }>;

  protected fieldConfig = inject(FIELD_CONFIG_INJECTION);
  protected asyncPreconditionsForEdition$ = inject(FIELD_ASYNC_PRECONDITIONS_INJECTION);
  protected sectionEntityType = <EntityTypeEnum>inject(SECTION_ENTITY_TYPE_INJECTION);
  protected translate = inject(TranslateService);

  private touchScreenQuery: MediaQueryList;

  constructor() {
    super();
    this.viewToggled = this.validationService.validateConditions(this.entity, this.fieldConfig.conditionsToView)
      && this.accessManager.hasFieldReadAccess(this.fieldConfig.code, this.fieldConfig.fieldEntityType);
    this.editToggled = this.validationService.validateConditions(this.entity, this.fieldConfig.conditionsToEdit);
    this.isSingleField = this.data.isSingleField;
    this.fieldGroupCode = this.data.fieldGroupCode;
    this.propertiesPath = this.data.propertiesPath;
    this.focusable = this.data.focusable;
    this.touchScreenQuery = this.media.matchMedia('(pointer: coarse)');

    this.calcFieldInitValue();

    // If any field among the fieldgroup is not undefined, the group must be displayed
    if (this.fieldInitValue !== null && this.fieldInitValue !== void 0) {
      this.getFieldGroupInfo(['isDisplayed']).next(true);
    }
  }

  /**
   * On touch screen devices only, toggle the tooltip after pressing the icon.
   * @param tooltip MatTooltip to toggle.
   */
  public onClickTooltip(tooltip: MatTooltip): void {
    if (this.touchScreenQuery.matches) {
      tooltip.toggle();
    }
  }

  /**
   * Reacts to any event on a focused element
   * @param {Event} event
   */
  public onActivate(event: Event): void {
    this.activate();
    event.stopImmediatePropagation();
    event.stopPropagation();
  }

  /**
   * Responds to an Escape keypress on a focused element
   * @param {Event} event
   */
  public onEscapeCancel(event: Event): void {
    this.cancel();
    event.stopImmediatePropagation();
    event.stopPropagation();
  }

  /**
   * Sets the current state of the field as saving
   * and copies the data from the current value to the initialvalue
   */
  public save(): void {
    if (this.isSingleField) {
      this.getFieldGroupState().next(this.FieldMode.SAVING);
      this.form?.markAsPristine();
      this.formStateService.save(this.fieldConfig, this.fieldGroupCode, this.data.formId, this.propertiesPath);
    }
  }

  /**
   * Adds indirection to the save logic if need be by separating the save event from the save logic
   */
  public onClickSave(): void {
    this.analyticsService.trackInlineActionEvent(this.fieldGroupCode, ActionEnum.SAVE, this.eventsOrigin);
    this.save();
  }

  /**
   * Replaces the data of the current value with the data in the initialvalue
   * and retrieves the next state based on whether the current value is empty or not
   */
  public cancel(): void {
    this.setFieldValue(this.getFieldInitialValue());
    this.getNextState();
  }

  /**
   * Adds indirection to the cancel logic because otherwise all fields would send
   * a cancel analytics event when switching from one field to another
   */
  public onClickCancel(): void {
    this.analyticsService.trackInlineActionEvent(this.fieldGroupCode, ActionEnum.CANCEL, this.eventsOrigin);
    this.cancel();
  }

  /**
   * Enables the field to be edited in whichever way it functions
   */
  public edit(): void {
    this.analyticsService.trackInlineActionEvent(this.fieldGroupCode, ActionEnum.SELECT, this.eventsOrigin);
    this.singleEditService.singleEditSubject.next(this.fieldGroupCode);
    this.requestFocus = true;
    this.getFieldGroupState().next(this.FieldMode.EDIT);
  }

  /**
   * Sets the field in read mode which can be clicked to turn it into the edit mode
   */
  public read(): void {
    this.getFieldGroupState().next(this.FieldMode.READ);
  }

  /**
   * Displays a link which can be clicked to turn it into the edit mode
   */
  public empty(): void {
    this.getFieldGroupState().next(this.FieldMode.EMPTY);
  }

  /**
   * Translate the value if there is a key in the po files. Otherwise, return the value.
   * @param value
   */
  public translateValue(value: string): string {
    return translateValueBuilder(this.translate)(value, this.fieldConfig.fieldCode);
  }

  // TODO TTT-4135 Remove, iterate over errors instead of validators
  /**
   * List of validators for which an error has been raised by the corresponding validator function.
   */
  public get erroredValidators(): FieldValidator[] {
    return this.fieldConfig.field.validators.filter((validator) => !!this.form.controls.field.errors?.[validator.code]);
  }

  protected calcFieldInitValue(): void {
    this.fieldInitValue =
      (!this.isCreatingEntity && this.fieldConfig) ? getValue(this.entity, this.fieldConfig.fieldPath) : undefined;
  }

  /**
   * Set up the hooks to which the field must react in order to update itself
   */
  protected setupHooks(): void {
    // Hook for a field switching to the edit mode
    this.singleEditService.singleEditSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((groupCode?: string) => {
      // Every other field that is not concerned is cancelled
      if (this.fieldGroupCode !== groupCode) {
        this.cancel();
      }
    });

    // Hook for the state change of a field group
    this.getFieldGroupState()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newMode) => {
        switch (newMode) {
          case FieldStateMode.AFTER_SAVE:
            this.refreshEntity();
            this.setFieldGroupInfo(['initialValue'], structuredClone(this.getFieldGroupInfo(['value'])));
            this.getNextState();
            break;
          case FieldStateMode.ERROR:
            this.setFieldGroupInfo(['value'], structuredClone(this.getFieldGroupInfo(['initialValue'])));
            this.getNextState();
            break;
          default:
            this.currentMode = newMode;
        }
      });

    /*
    For every field in conditionsToView, subscribe to the observable associated with its code.
    Ex. RenovationYear subscribes to changes to ConstructionYear
     */
    this.fieldConfig.conditionsToView.forEach((condition) => {
      const conditionFieldCode = condition.field.split('.').last();
      this.getFormChanges([conditionFieldCode])
        ?.pipe(
          // Avoid to do calculation if the value doesn't change
          distinctUntilChanged(fastDeepEqual),
          // Avoid to do calculation for every changes
          debounceTime(200),
          // Stop listening if the form is destroyed
          takeUntilDestroyed(this.destroyRef)
        )
        ?.subscribe(({path, value}) => {
          // Fetch a copy of the current entity
          const unsavedEntity = structuredClone(this.entity);
          // Apply the new value in the unsaved entity
          setValue(unsavedEntity, path, value);
          // Validate conditions against this new unsaved entity
          this.viewToggled = this.validationService.validateConditions(
            unsavedEntity,
            this.fieldConfig.conditionsToView
          );
          this.editToggled = this.validationService.validateConditions(
            unsavedEntity,
            this.fieldConfig.conditionsToEdit
          );
          // If the conditions are not met, reset the field
          if (!this.editToggled || !this.viewToggled) {
            this.doReset();
          }
        });
    });

    this.initialiseValueChangeSubject();
  }

  /**
   * Resets the field by clearing the value. Can be overridden for specific fields with specific behaviours.
   */
  protected doReset(): void {
    // Resetting the form's field also resets the value in the formStateService
    this.form.controls.field.reset('');
  }

  /**
   * Initialise the value change subject in the formStateService. Can be overriden for specific fields with specific behaviours.
   */
  protected initialiseValueChangeSubject(): void {
    this.formStateService.setPath(
      ['changes', this.fieldConfig.fieldCode],
      new BehaviorSubject({path: this.fieldConfig.fieldPath, value: this.fieldInitValue})
    );
  }

  /**
   * Get a validator function which combines all validators from the field's config.
   * @param data Additional data that can be passed to composing validators when required.
   * @returns Composed validator function.
   */
  protected computeValidators(data?: { [key: string]: any }): ValidatorFn {
    return Validators.compose(
      this.fieldConfig.field?.validators.map((validator) => {
        return this.validationService.getValidator(validator, this.entity, data);
      })
    );
  }

  /**
   * Uses Angular's composition of async validators on all async validators from the field configuration
   * @returns {ValidatorFn}
   */
  protected computeAsyncValidators(): AsyncValidatorFn {
    return Validators.composeAsync(
      this.fieldConfig.field?.validators.map((validator) => {
        return this.validationService.getAsyncValidator(validator, this.entity, this.fieldConfig.fieldPath, this.fieldConfig.fieldEntityType);
      })
    );
  }

  /**
   * Checks the current value of a field and determines which state it should be next
   * based on if the value is an empty array or undefined
   */
  protected getNextState(): void {
    const isEmpty = Object.values(this.getFieldGroupInfo(['value'])).every((value) => {
      return (Array.isArray(value) && value.length === 0) || value == void 0 || value === '';
    });
    if (isEmpty) {
      this.empty();
    } else {
      this.read();
    }
  }

  /**
   * Either activate the field into editing mode or save it if you were editing it
   */
  protected activate(): void {
    if (this.currentMode === this.FieldMode.EDIT && this.form.valid) {
      this.save();
    } else if (this.accessManager.hasAllNeededPermissions(this.permissionsForEdition) && this.preconditionsForEdition) {
      this.edit();
    }
  }

  protected refreshEntity(): void {
    this.entity = this.propertiesPath
      ? this.appManager.currentEntity[this.propertiesPath]
      : this.appManager.currentEntity;
  }

  /**
   * Retrieves data about the field group, stored at the provided path in the FormStateService.
   * @param keys Path where the data is stored.
   * @return Data at given path.
   */
  public getFieldGroupInfo(keys: string[]): any {
    return this.formStateService.getPath([this.fieldGroupCode, ...keys], {
      customPath: this.propertiesPath
    });
  }

  /**
   * Retrieves the field's value from the FormStateService.
   * @return Field's value.
   */
  public getFieldValue(): any {
    return this.getFieldGroupInfo(['value', this.fieldConfig.fieldCode]);
  }

  /**
   * Retrieves the field's initial value from the FormStateService.
   * @return Field's initial value.
   */
  public getFieldInitialValue(): any {
    return this.getFieldGroupInfo(['initialValue', this.fieldConfig.fieldCode]);
  }

  /**
   * Retrieves the field group's state from the FormStateService.
   * @return Field group's state.
   */
  public getFieldGroupState(): Subject<FieldStateMode> {
    return this.getFieldGroupInfo(['state']);
  }

  /**
   * Stores value at the given path in the FormStateService.
   * @param keys Path where to store value.
   * @param value Data to be stored.
   */
  public setFieldGroupInfo(keys: string[], value: any): void {
    this.formStateService.setPath([this.fieldGroupCode, ...keys], value, {
      customPath: this.propertiesPath
    });
  }

  /**
   * Sets the field's value in the FormStateService.
   * @param value Value to be stored.
   */
  public setFieldValue(value: any): void {
    this.setFieldGroupInfo(['value', this.fieldConfig.fieldCode], value);
    // Emit change message if changes exists
    if (this.getFormChanges([this.fieldConfig.fieldCode])) {
      this.getFormChanges([this.fieldConfig.fieldCode]).next({path: this.fieldConfig.fieldPath, value: value});
    }
  }

  /**
   * Sets the field's initial value in the FormStateService.
   * @param value Initial value to be stored.
   */
  public setFieldInitialValue(value: any): any {
    this.setFieldGroupInfo(['initialValue', this.fieldConfig.fieldCode], value);
  }

  public focus(): void {
    //Do nothing
  }

  /**
   * Returns if the field has requested focus by user interaction
   */
  public hasRequestedFocus(): boolean {
    return this.requestFocus;
  }

  /**
   * Resets state of field focus
   */
  public resetRequestFocus(): void {
    this.requestFocus = false;
  }

  /**
   * Render fieldConfig's CSS properties if the field has a value.
   * @return An object containing css styles to apply
   */
  public get customCss(): { [cssProperty: string]: string } {
    if (!this.getFieldValue()) return {};
    return customCssBuilderForEntity(this.fieldConfig.customCss, this.entity);
  }
}
